// The in-memory twin of BIZ-PLAYBOOKS and BIZ-CLIENT-HEALTH (contracts/api/business.ts). The
// rules are the server's (services/api/src/domain/business/*):
//   - a playbook is a doc (SOP, or any page with links): its owner seat, follower seats and run
//     templates (`PUT pages/{id}/playbook`, the whole set, the page's editors); its runs are the
//     tasks made from those templates in 90 days, "followed" = done with every checklist item
//     ticked; `pending_updates` counts the pending `doc_update` approvals on the page;
//   - a client's health is the weighted mean of the signals with data (health-score.ts, copied
//     below: the web never imports the API), null while only the open-issue count has any; the
//     twin knows due/late and finished tasks, its own client issues and the survey answers (no
//     reply times, no reopen history, no daily snapshots: those read as "no data" / no trend);
//   - a survey is answered once behind its link (`/survey/{co}/{token}`, no account); a
//     detractor's answer (NPS 0–6, CSAT 1–2) raises a client issue.
// Everything is read from the other twins through `call` (the composed handlers, as the caller),
// so visibility is theirs. `seedDocUpdate` files one DE's proposed new version of a seeded SOP
// (the approvals inbox's `doc_update` card). `fake-business-contract.test.ts` holds it to the
// contracts.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, type Principal, problem } from "./fake";
import { NO_EXECUTION } from "./fake-approval-execution";
import { type FakeCompany, type FakeDe, uid } from "./fake-data";
import type { WorkBridge } from "./fake-work";

type Playbook = OperationOutput<"pages.playbook">;
type PlaybookSeat = Playbook["followers"][number];
type ClientHealth = OperationOutput<"clients.healthGet">;
type Signal = ClientHealth["signals"][number];
type SurveyView = ClientHealth["surveys"][number];
type Approval = OperationOutput<"my.approvals">["data"][number];
type ChartSeat = {
  id: string;
  title: string;
  status: string;
  holders: { membership_id: string; membership_kind: string; display_name: string }[];
};
type SpaceLite = {
  id: string;
  name: string;
  kind: string;
  archived_at: string | null;
  settings: { account_seat_id?: string | null } & Record<string, unknown>;
};
type PageLite = {
  id: string;
  title: string;
  kind: string;
  version: number;
  space_id: string;
  updated_at: string;
  archived_at: string | null;
  actor_kind?: string;
  actor_membership_id?: string | null;
  can_edit?: boolean;
};

type Links = { owner: string | null; followers: string[]; templates: string[] };
type SurveyRow = SurveyView & { company_id: string; token: string };
type ClientIssue = {
  id: string;
  space_id: string;
  title: string;
  status: string;
  created_at: string;
};

const DAY = 86_400_000;
const RUN_WINDOW_DAYS = 90;
const SURVEY_DAYS = 30;
const validation = (detail: string) =>
  problem(422, "validation_failed", "Validation failed", detail);

// health-score.ts (services/api/src/domain/business), the pure part --------------------------------

const WEIGHTS = { delays: 25, rework: 15, issues: 15, response: 15, sentiment: 30 } as const;
const pct = (part: number, whole: number) => Math.round((part / whole) * 100);
const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

export function npsOf(scores: readonly number[]): number | null {
  if (scores.length === 0) return null;
  const promoters = scores.filter((s) => s >= 9).length;
  const detractors = scores.filter((s) => s <= 6).length;
  return Math.round(((promoters - detractors) / scores.length) * 100);
}

const isDetractor = (kind: "nps" | "csat", score: number) =>
  kind === "nps" ? score <= 6 : score <= 2;

function signalsOf(i: {
  due: number;
  late: number;
  finished: number;
  reopened: number;
  openIssues: number;
  nps: number[];
  csat: number[];
}): Signal[] {
  const answers = [
    ...i.nps.map((s) => (s >= 9 ? 100 : s >= 7 ? 50 : 0)),
    ...i.csat.map((s) => (s - 1) * 25),
  ];
  const nps = npsOf(i.nps);
  return [
    {
      key: "delays",
      label: "On-time work",
      score: i.due > 0 ? clamp(100 - pct(i.late, i.due)) : null,
      weight: WEIGHTS.delays,
      detail: i.due > 0 ? `${i.late} of ${i.due} due in 30 days late` : "Nothing due in 30 days",
    },
    {
      key: "rework",
      label: "Rework",
      score:
        i.finished + i.reopened > 0 ? clamp(100 - pct(i.reopened, i.finished + i.reopened)) : null,
      weight: WEIGHTS.rework,
      detail:
        i.finished + i.reopened > 0
          ? `${i.reopened} reopened, ${i.finished} finished in 30 days`
          : "Nothing finished in 30 days",
    },
    {
      key: "issues",
      label: "Open issues",
      score: clamp(100 - 25 * i.openIssues),
      weight: WEIGHTS.issues,
      detail: i.openIssues === 1 ? "1 open issue" : `${i.openIssues} open issues`,
    },
    {
      key: "response",
      label: "Reply time",
      score: null,
      weight: WEIGHTS.response,
      detail: "No conversation in 30 days",
    },
    {
      key: "sentiment",
      label: "NPS / CSAT",
      score: answers.length > 0 ? clamp(answers.reduce((a, b) => a + b, 0) / answers.length) : null,
      weight: WEIGHTS.sentiment,
      detail:
        answers.length === 0
          ? "No answers in 90 days"
          : `${answers.length} answer${answers.length === 1 ? "" : "s"} in 90 days${nps === null ? "" : `, NPS ${nps}`}`,
    },
  ];
}

const bandOf = (score: number | null): ClientHealth["band"] =>
  score === null ? "none" : score >= 75 ? "green" : score >= 50 ? "amber" : "red";

function scoreOf(signals: readonly Signal[]): number | null {
  const scored = signals.filter((s) => s.score !== null);
  if (!scored.some((s) => s.key !== "issues")) return null;
  const w = scored.reduce((a, s) => a + s.weight, 0);
  return clamp(scored.reduce((a, s) => a + (s.score as number) * s.weight, 0) / w);
}

// The twin -------------------------------------------------------------------------------------------

export function createBusinessFake(deps: {
  now: () => Date;
  companies: FakeCompany[];
  work: () => WorkBridge | null;
  /** A composed handler's JSON answer, as the caller (undefined: refused or async). */
  call: <T>(id: RouteId, ctx: Ctx) => T | undefined;
  approvalsOf: (companyId: string) => readonly Approval[];
}): {
  handlers: Partial<Record<RouteId, Handler>>;
  seedDocUpdate: (
    company: FakeCompany,
    des: FakeDe[],
    pages: readonly { id: string; title: string; kind: string; version: number }[],
  ) => Approval | null;
} {
  let seq = 0;
  const next = () => uid(0xb1a0, ++seq);
  const stamp = () => deps.now().toISOString();
  const links = new Map<string, Links>(); // `${co}:${page}`
  // owner approval per playbook (`${co}:${page}`); unset: on for an SOP (a new playbook), else off
  const approvals = new Map<string, boolean>();
  const ownerApprovalOf = (co: string, pg: { id: string; kind: string }) =>
    approvals.get(`${co}:${pg.id}`) ?? pg.kind === "sop";
  const surveys: SurveyRow[] = [];
  const issues: ClientIssue[] = [];

  const sub = (ctx: Ctx, path: string, params: Record<string, string> = {}): Ctx => ({
    ...ctx,
    params: { ...ctx.params, ...params },
    url: new URL(`http://fake.local${path}${path.includes("?") ? "&" : "?"}limit=200`),
    body: undefined,
  });
  const linksOf = (co: string, page: string): Links =>
    links.get(`${co}:${page}`) ?? { owner: null, followers: [], templates: [] };
  const chartSeats = (ctx: Ctx) =>
    deps.call<{ seats: ChartSeat[] }>("orgChart.get", sub(ctx, "/org-chart"))?.seats ?? [];
  const seatOf = (seats: ChartSeat[], id: string): PlaybookSeat | null => {
    const s = seats.find((x) => x.id === id);
    if (!s) return null;
    return {
      seat_id: s.id,
      title: s.title,
      holders: s.holders
        .filter((h) => h.membership_kind === "person" || h.membership_kind === "digital_employee")
        .map((h) => ({
          membership_id: h.membership_id,
          display_name: h.display_name,
          kind: h.membership_kind === "digital_employee" ? "digital_employee" : "person",
        })),
    };
  };
  const nameOf = (seats: ChartSeat[], membershipId: string | null | undefined) =>
    seats.flatMap((s) => s.holders).find((h) => h.membership_id === membershipId)?.display_name ??
    null;
  /** Templates the caller can see, by id: every live board's (`itemTemplates.list`). */
  const templatesOf = (ctx: Ctx, p: Principal) => {
    const boards = (deps.work()?.stateOf(p.company.id)?.boards ?? []).filter((b) => !b.archived_at);
    return boards.flatMap((b) =>
      (
        deps.call<{ data: { id: string; name: string; board_id: string }[] }>(
          "itemTemplates.list",
          sub(ctx, `/boards/${b.id}/item-templates`, { id: b.id }),
        )?.data ?? []
      ).map((t) => ({ id: t.id, name: t.name, board_id: b.id, board_name: b.name })),
    );
  };
  const runsOf = (p: Principal, templateIds: string[]): Playbook["runs"] => {
    if (!templateIds.length) return { started: 0, done: 0, followed: 0, followed_pct: null };
    const since = deps.now().getTime() - RUN_WINDOW_DAYS * DAY;
    const runs = (deps.work()?.tasksOf(p.company.id) ?? []).filter((t) => {
      const ref = t.origin_ref as { item_template_id?: string } | null;
      return (
        !t.deleted &&
        t.origin === "template" &&
        Date.parse(t.created_at) >= since &&
        templateIds.includes(ref?.item_template_id ?? "")
      );
    });
    const done = runs.filter((t) => t.completed_at !== null);
    const followed = done.filter((t) => t.checklist_items.every((i) => i.done_at !== null)).length;
    return {
      started: runs.length,
      done: done.length,
      followed,
      followed_pct: done.length > 0 ? Math.round((followed / done.length) * 100) : null,
    };
  };
  const pendingOf = (co: string, pageId: string) =>
    deps
      .approvalsOf(co)
      .filter((a) => a.kind === "doc_update" && a.state === "pending" && a.subject_id === pageId)
      .length;

  function playbookOf(ctx: Ctx, p: Principal, page: PageLite): Playbook {
    const seats = chartSeats(ctx);
    const l = linksOf(p.company.id, page.id);
    const all = l.templates.length ? templatesOf(ctx, p) : [];
    const readers =
      deps.call<{ data: Playbook["readers"] } | Playbook["readers"]>(
        "pages.readers",
        sub(ctx, `/pages/${page.id}/readers`, { id: page.id }),
      ) ?? [];
    const by = nameOf(seats, page.actor_membership_id);
    return {
      page_id: page.id,
      title: page.title,
      kind: page.kind,
      version: page.version,
      owner: l.owner ? seatOf(seats, l.owner) : null,
      followers: l.followers.flatMap((s) => seatOf(seats, s) ?? []),
      readers: Array.isArray(readers) ? readers : readers.data,
      templates: l.templates.flatMap((t) => all.find((x) => x.id === t) ?? []),
      updated_at: page.updated_at,
      updated_by: by
        ? { display_name: by, kind: page.actor_kind ?? "person" }
        : page.actor_kind === "system"
          ? { display_name: "System", kind: "system" }
          : null,
      runs: runsOf(p, l.templates),
      pending_updates: pendingOf(p.company.id, page.id),
      can_edit: Boolean(page.can_edit) && page.archived_at === null,
      owner_approval: ownerApprovalOf(p.company.id, page),
    };
  }

  // Client health -----------------------------------------------------------------------------------

  const clientSpaces = (ctx: Ctx) =>
    (
      deps.call<{ data: SpaceLite[] }>("spaces.list", sub(ctx, "/spaces?kind=client"))?.data ?? []
    ).filter((s) => s.kind === "client" && !s.archived_at);
  const surveyView = ({ company_id: _c, token: _t, ...v }: SurveyRow): SurveyView => v;

  function healthOf(ctx: Ctx, p: Principal, s: SpaceLite): ClientHealth {
    const now = deps.now().getTime();
    const w = deps.work()?.stateOf(p.company.id);
    const boards = new Set((w?.boards ?? []).filter((b) => b.space_id === s.id).map((b) => b.id));
    const tasks = (w?.tasks ?? []).filter((t) => boards.has(t.board_id) && !t.deleted);
    const due = tasks.filter((t) => {
      const d = t.due_at ? Date.parse(t.due_at) : null;
      return d !== null && d <= now && d >= now - 30 * DAY;
    });
    const late = due.filter(
      (t) => t.completed_at === null || Date.parse(t.completed_at) > Date.parse(t.due_at ?? ""),
    );
    const finished = tasks.filter(
      (t) => t.completed_at !== null && Date.parse(t.completed_at) >= now - 30 * DAY,
    );
    const open = issues.filter((i) => i.space_id === s.id && i.status !== "resolved");
    const answered = surveys.filter(
      (v) =>
        v.company_id === p.company.id &&
        v.space_id === s.id &&
        v.score !== null &&
        Date.parse(v.answered_at ?? "") >= now - 90 * DAY,
    );
    const nps = answered.filter((v) => v.kind === "nps").map((v) => v.score as number);
    const signals = signalsOf({
      due: due.length,
      late: late.length,
      finished: finished.length,
      reopened: 0,
      openIssues: open.length,
      nps,
      csat: answered.filter((v) => v.kind === "csat").map((v) => v.score as number),
    });
    const score = scoreOf(signals);
    const seatId = s.settings.account_seat_id ?? null;
    const seat = seatId ? chartSeats(ctx).find((x) => x.id === seatId) : undefined;
    const at = stamp();
    return {
      space_id: s.id,
      name: s.name,
      account_seat: seat ? { seat_id: seat.id, title: seat.title } : null,
      score,
      band: bandOf(score),
      trend: null,
      signals,
      nps: npsOf(nps),
      computed_at: at,
      surveys: surveys
        .filter((v) => v.company_id === p.company.id && v.space_id === s.id)
        .sort((a, b) => b.sent_at.localeCompare(a.sent_at))
        .slice(0, 20)
        .map(surveyView),
      history: [{ day: at.slice(0, 10), score, band: bandOf(score) }],
      open_issues: open.map(({ id, title, status, created_at }) => ({
        id,
        title,
        status,
        created_at,
      })),
    };
  }

  const respondent = (v: SurveyRow) => {
    const company = deps.companies.find((c) => c.id === v.company_id);
    const space = deps
      .work()
      ?.spacesOf(v.company_id)
      ?.find((s) => s.id === v.space_id);
    return {
      company_name: company?.name ?? "",
      client_name: space?.name ?? "",
      kind: v.kind,
      recipient_name: v.recipient_name,
      answered: v.answered_at !== null,
      expired: v.answered_at === null && Date.parse(v.expires_at) <= deps.now().getTime(),
    };
  };
  const byToken = (ctx: Ctx) =>
    surveys.find((v) => v.company_id === ctx.params.co && v.token === ctx.params.token);

  const handlers: Partial<Record<RouteId, Handler>> = {
    "pages.playbook": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const page = deps.call<PageLite>("pages.get", sub(ctx, `/pages/${ctx.params.id}`));
      if (!page) return notFound();
      return json(playbookOf(ctx, p, page));
    },
    "pages.playbookSet": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const page = deps.call<PageLite>("pages.get", sub(ctx, `/pages/${ctx.params.id}`));
      if (!page) return notFound();
      if (!page.can_edit) return problem(403, "forbidden", "Forbidden");
      if (page.kind === "task_body" || page.kind === "board_note")
        return validation("page: a task body or a board note is not a playbook");
      const b = (ctx.body ?? {}) as Partial<{
        owner_seat_id: string | null;
        follower_seat_ids: string[];
        item_template_ids: string[];
        owner_approval: boolean;
      }>;
      const owner = b.owner_seat_id ?? null;
      const followers = [...new Set(b.follower_seat_ids ?? [])];
      const templates = [...new Set(b.item_template_ids ?? [])];
      const open = new Set(
        chartSeats(ctx)
          .filter((s) => s.status !== "closed")
          .map((s) => s.id),
      );
      if (owner && !open.has(owner))
        return validation("owner_seat_id: no such open seat in this company");
      if (followers.some((s) => !open.has(s)))
        return validation("follower_seat_ids: no such open seat in this company");
      if (templates.length) {
        const known = new Set(templatesOf(ctx, p).map((t) => t.id));
        if (templates.some((t) => !known.has(t)))
          return validation("item_template_ids: no such item template");
      }
      links.set(`${p.company.id}:${page.id}`, { owner, followers, templates });
      if (typeof b.owner_approval === "boolean")
        approvals.set(`${p.company.id}:${page.id}`, b.owner_approval);
      return json(playbookOf(ctx, p, page));
    },
    "playbooks.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const seat = ctx.url.searchParams.get("seat_id");
      const pages = (
        deps.call<{ data: PageLite[] }>("pages.list", sub(ctx, "/pages"))?.data ?? []
      ).filter((pg) => !pg.archived_at && pg.kind !== "task_body");
      const seats = chartSeats(ctx);
      const rows = pages
        .filter((pg) => {
          const l = linksOf(p.company.id, pg.id);
          return seat
            ? l.owner === seat || l.followers.includes(seat)
            : pg.kind === "sop" || l.owner !== null || l.followers.length + l.templates.length > 0;
        })
        .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()))
        .map((pg) => {
          const l = linksOf(p.company.id, pg.id);
          return {
            page_id: pg.id,
            title: pg.title,
            space_id: pg.space_id,
            owner: l.owner ? seatOf(seats, l.owner) : null,
            follower_count: l.followers.length,
            updated_at: pg.updated_at,
            followed_pct: runsOf(p, l.templates).followed_pct,
            pending_updates: pendingOf(p.company.id, pg.id),
            owner_approval: ownerApprovalOf(p.company.id, pg),
          };
        });
      return json({ data: rows });
    },
    "clients.health": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      return json({
        data: clientSpaces(ctx).map((s) => {
          const { surveys: _s, history: _h, open_issues: _o, ...h } = healthOf(ctx, p, s);
          return h;
        }),
      });
    },
    "clients.healthGet": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = clientSpaces(ctx).find((x) => x.id === ctx.params.id);
      return s ? json(healthOf(ctx, p, s)) : notFound();
    },
    "clients.surveyCreate": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = clientSpaces(ctx).find((x) => x.id === ctx.params.id);
      if (!s) return notFound();
      const b = (ctx.body ?? {}) as {
        kind?: string;
        recipient_name?: string;
        recipient_email?: string | null;
      };
      if (b.kind !== "nps" && b.kind !== "csat") return validation("kind: nps or csat");
      const name = (b.recipient_name ?? "").trim().slice(0, 200);
      if (!name) return validation("recipient_name: required");
      const email = b.recipient_email?.trim().toLowerCase() || null;
      if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
        return validation("recipient_email: not an email address");
      const now = deps.now();
      const token = `sv_${crypto.randomUUID().replaceAll("-", "")}`;
      const row: SurveyRow = {
        company_id: p.company.id,
        token,
        id: next(),
        space_id: s.id,
        kind: b.kind,
        recipient_name: name,
        recipient_email: email,
        sent_at: now.toISOString(),
        mailed_at: email ? now.toISOString() : null,
        expires_at: new Date(now.getTime() + SURVEY_DAYS * DAY).toISOString(),
        score: null,
        comment: null,
        answered_at: null,
        issue_id: null,
      };
      surveys.push(row);
      return json({ survey: surveyView(row), link: `/survey/${p.company.id}/${token}` }, 201);
    },
    "survey.get": (ctx) => {
      const v = byToken(ctx);
      return v ? json(respondent(v)) : notFound();
    },
    "survey.answer": (ctx) => {
      const v = byToken(ctx);
      if (!v) return notFound();
      const space = deps
        .work()
        ?.spacesOf(v.company_id)
        ?.find((s) => s.id === v.space_id);
      if (!space || space.archived_at)
        return problem(404, "not_found", "Not found", "this survey is closed");
      if (v.answered_at !== null)
        return problem(409, "conflict", "Conflict", "this survey was already answered");
      if (Date.parse(v.expires_at) <= deps.now().getTime())
        return problem(404, "not_found", "Not found", "this survey link has expired");
      const b = (ctx.body ?? {}) as { score?: unknown; comment?: unknown };
      const [min, max] = v.kind === "nps" ? [0, 10] : [1, 5];
      const score = b.score;
      if (typeof score !== "number" || !Number.isInteger(score) || score < min || score > max)
        return validation(`score: ${min} to ${max}`);
      const comment =
        typeof b.comment === "string" ? b.comment.trim().slice(0, 2000) || null : null;
      const at = stamp();
      if (isDetractor(v.kind, score)) {
        const client = respondent(v).client_name;
        const issue: ClientIssue = {
          id: next(),
          space_id: v.space_id,
          title: `${client}: ${v.kind === "nps" ? "NPS" : "CSAT"} ${score} from ${v.recipient_name}`,
          status: "identify",
          created_at: at,
        };
        issues.push(issue);
        v.issue_id = issue.id;
      }
      Object.assign(v, { score, comment, answered_at: at });
      return json(respondent(v));
    },
  };

  /** One DE's proposed new version of the company's first SOP, waiting for its owner. */
  function seedDocUpdate(
    company: FakeCompany,
    des: FakeDe[],
    pages: readonly { id: string; title: string; kind: string; version: number }[],
  ): Approval | null {
    const de = des[0];
    const sop = pages.find((x) => x.kind === "sop");
    if (!de || !sop) return null;
    const t = new Date(deps.now().getTime() - 3 * 3600_000).toISOString();
    const body = [
      "## Purpose",
      `How we run ${sop.title.toLowerCase()} so every candidate gets the same call.`,
      "",
      "## Steps",
      "1. Confirm the time by message the day before",
      "2. Run the call from the phone screen script",
      "3. Score the call within one hour",
      "4. Log the notice period and salary range on the task",
      "",
      "## Done when",
      "The score and notes are on the task and the next step is booked.",
    ].join("\n");
    return {
      company_id: company.id,
      id: uid(0xd0c0, 1),
      kind: "doc_update",
      subject_kind: "page",
      subject_id: sop.id,
      requested_by_seat_id: de.seat_id,
      requested_by_membership_id: de.membership_id,
      approver_seat_id: de.supervisor.seat_id,
      run_id: null,
      proposal: {
        title: `Update “${sop.title}”`,
        summary: "Two of the last five runs skipped scoring; the steps now say when to score.",
        change: {
          page_id: sop.id,
          body_markdown: body,
          reason: "Two of the last five runs skipped scoring; the steps now say when to score.",
          owner_seat_id: null,
          precondition: {
            kind: "version",
            subject_kind: "page",
            subject_id: sop.id,
            version: sop.version,
          },
        },
      },
      state: "pending",
      approved_payload: null,
      decided_at: null,
      decided_by_membership_id: null,
      reason: null,
      thread_id: null,
      expires_at: new Date(deps.now().getTime() + 3 * DAY).toISOString(),
      executed_at: null,
      ...NO_EXECUTION,
      actor_kind: "digital_employee",
      actor_membership_id: de.membership_id,
      created_at: t,
      updated_at: t,
    };
  }

  return { handlers, seedDocUpdate };
}
